import React, { useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Mail,
  Edit,
  MessageSquare,
  ExternalLink,
  Users,
} from 'lucide-react';

interface Student {
  id: string;
  name: string;
  avatar?: string;
  attendancePercentage: number;
  missedMeetings: number;
  submissionProgress: { completed: number; total: number };
}

interface AttendanceCourse {
  id: string;
  name: string;
  cohort: string;
  status: 'active' | 'completed';
  teacher: { id: string; name: string; avatar?: string };
  studentCount: number;
  progress: number;
  attendancePercentage: number;
  students: Student[];
}

const mockAttendanceData: AttendanceCourse[] = [
  {
    id: '1',
    name: 'Mathematics Advanced',
    cohort: 'Year 1 / Cohort A2',
    status: 'active',
    teacher: { id: '1', name: 'Sarah Cohen' },
    studentCount: 25,
    progress: 45,
    attendancePercentage: 89,
    students: [
      { id: 's1', name: 'Yossi Levy', attendancePercentage: 95, missedMeetings: 1, submissionProgress: { completed: 12, total: 14 } },
      { id: 's2', name: 'Maya Cohen', attendancePercentage: 88, missedMeetings: 2, submissionProgress: { completed: 10, total: 14 } },
      { id: 's3', name: 'Daniel Green', attendancePercentage: 75, missedMeetings: 4, submissionProgress: { completed: 8, total: 14 } },
      { id: 's4', name: 'Noa Shapiro', attendancePercentage: 100, missedMeetings: 0, submissionProgress: { completed: 14, total: 14 } },
      { id: 's5', name: 'Avi Brown', attendancePercentage: 82, missedMeetings: 3, submissionProgress: { completed: 11, total: 14 } },
    ],
  },
  {
    id: '2',
    name: 'Physics Fundamentals',
    cohort: 'Year 1 / Cohort B1',
    status: 'active',
    teacher: { id: '2', name: 'David Levi' },
    studentCount: 22,
    progress: 60,
    attendancePercentage: 92,
    students: [
      { id: 's6', name: 'Rachel Gold', attendancePercentage: 98, missedMeetings: 0, submissionProgress: { completed: 9, total: 12 } },
      { id: 's7', name: 'Eli Fischer', attendancePercentage: 85, missedMeetings: 2, submissionProgress: { completed: 7, total: 12 } },
    ],
  },
  {
    id: '3',
    name: 'History of Israel',
    cohort: 'Year 2 / Cohort A1',
    status: 'completed',
    teacher: { id: '3', name: 'Rachel Green' },
    studentCount: 28,
    progress: 100,
    attendancePercentage: 94,
    students: [],
  },
];

const grades = ['All Grades', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

export function AttendanceTab() {
  const [courses] = useState<AttendanceCourse[]>(mockAttendanceData);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('All Grades');
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);

  const filteredCourses = courses.filter((course) =>
    course.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleExpand = (courseId: string) => {
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };

  const getAttendanceBadgeColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-green-100 text-green-700';
    if (percentage >= 75) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Grade Filter */}
        <select
          value={selectedGrade}
          onChange={(e) => setSelectedGrade(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          {grades.map((grade) => (
            <option key={grade} value={grade}>{grade}</option>
          ))}
        </select>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
          />
        </div>
      </div>

      {/* Attendance List */}
      <div className="space-y-3">
        {filteredCourses.map((course) => (
          <div
            key={course.id}
            className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${
              course.status === 'completed' ? 'opacity-75' : ''
            }`}
          >
            {/* Course Header Row */}
            <div
              className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => handleToggleExpand(course.id)}
            >
              {/* Teacher Avatar */}
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">
                {course.teacher.name.split(' ').map(n => n[0]).join('')}
              </div>

              {/* Course Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900 truncate">{course.name}</h3>
                  {course.status === 'completed' && (
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      Completed
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{course.cohort}</p>
              </div>

              {/* Student Avatars Preview */}
              <div className="hidden md:flex items-center">
                <div className="flex -space-x-2">
                  {course.students.slice(0, 5).map((student, idx) => (
                    <div
                      key={student.id}
                      className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600"
                      style={{ zIndex: 5 - idx }}
                    >
                      {student.name.split(' ').map(n => n[0]).join('')}
                    </div>
                  ))}
                  {course.studentCount > 5 && (
                    <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-500">
                      +{course.studentCount - 5}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="hidden lg:flex items-center gap-4">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>{course.studentCount}</span>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAttendanceBadgeColor(course.attendancePercentage)}`}>
                  {course.attendancePercentage}% Attendance
                </span>
              </div>

              {/* Progress */}
              <div className="hidden lg:flex items-center gap-2 w-32">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full"
                    style={{ width: `${course.progress}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600">{course.progress}%</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="px-3 py-1 text-sm text-teal-600 hover:bg-teal-50 rounded-lg flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Course
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="Send mail to all"
                >
                  <Mail className="w-4 h-4" />
                </button>

                {/* Expand/Collapse Icon */}
                {expandedCourseId === course.id ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Expanded Students List */}
            {expandedCourseId === course.id && (
              <div className="border-t border-gray-200 bg-gray-50">
                <div className="p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Student Attendance</h4>
                  {course.students.length > 0 ? (
                    <div className="space-y-2">
                      {course.students.map((student) => (
                        <div
                          key={student.id}
                          className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200"
                        >
                          {/* Student Avatar */}
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                            {student.name.split(' ').map(n => n[0]).join('')}
                          </div>

                          {/* Student Name (clickable) */}
                          <button className="flex-1 text-left hover:text-teal-600">
                            <span className="font-medium text-gray-900">{student.name}</span>
                          </button>

                          {/* Attendance Percentage */}
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  student.attendancePercentage >= 90 ? 'bg-green-500' :
                                  student.attendancePercentage >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${student.attendancePercentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600 w-12">{student.attendancePercentage}%</span>
                          </div>

                          {/* Missed Meetings */}
                          <div className="text-sm text-gray-600 w-28">
                            {student.missedMeetings > 0 ? (
                              <span className="text-red-600">{student.missedMeetings} missed</span>
                            ) : (
                              <span className="text-green-600">No absences</span>
                            )}
                          </div>

                          {/* Submission Progress */}
                          <div className="text-sm text-gray-600 w-16">
                            <span className={student.submissionProgress.completed === student.submissionProgress.total ? 'text-green-600' : ''}>
                              {student.submissionProgress.completed}/{student.submissionProgress.total}
                            </span>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            <button
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                              title="Edit attendance"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                              title="Send message"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No student data available</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No courses found</p>
          </div>
        )}
      </div>
    </div>
  );
}
